/**
 * VB Server - Analytics API
 *
 * Analytics data for dashboards and reporting.
 *
 * @example
 * import { getVolume, getHeatmap, getTopSpeakers } from '@/api/server/analytics';
 *
 * const volume = await getVolume({ orgId, startTime, endTime });
 * const heatmap = await getHeatmap({ orgId });
 */

import { serverGet } from './client';

// =============================================================================
// Types
// =============================================================================

/**
 * Base options for analytics queries
 */
export interface AnalyticsOptions {
  orgId: string;
  channelIds?: string[];
  startTime?: string | number;
  endTime?: string | number;
}

/**
 * Volume data point
 */
export interface VolumeDataPoint {
  time: string;
  count: number;
}

/**
 * Heatmap data point (from backend get_heatmap function)
 * hw_id = dow * 24 + hour (0-167)
 */
export interface HeatmapDataPoint {
  hw_id: number; // Hour of week (0-167)
  doc_count: number;
}

/**
 * Top speaker data
 */
export interface TopSpeakerData {
  speaker_id: string;
  speaker_name?: string;
  message_count: number;
  total_duration?: number;
}

/**
 * Channel distribution data
 */
export interface ChannelDistributionData {
  channel_id: string;
  count: number;
}

/**
 * Summary statistics
 */
export interface SummaryStats {
  total_messages: number;
  total_duration: number;
  avg_confidence: number;
  unique_speakers: number;
}

/**
 * Alert statistics
 */
export interface AlertStats {
  total_alerts: number;
  triggered_count: number;
  top_keywords: Array<{ keyword: string; count: number }>;
}

/**
 * Volume data grouped by channel
 */
export type VolumeByChannelData = Record<string, VolumeDataPoint[]>;

/**
 * Alert keyword time series data
 */
export interface AlertKeywordData {
  keyword: string;
  data: VolumeDataPoint[];
  total: number;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Build query params for analytics endpoints
 */
function buildAnalyticsParams(options: AnalyticsOptions): URLSearchParams {
  const params = new URLSearchParams();
  params.set('org_id', options.orgId);

  if (options.channelIds?.length) {
    params.set('channel_ids', options.channelIds.join(','));
  }
  if (options.startTime) {
    const start =
      typeof options.startTime === 'number'
        ? new Date(options.startTime).toISOString()
        : options.startTime;
    params.set('start', start);
  }
  if (options.endTime) {
    const end =
      typeof options.endTime === 'number'
        ? new Date(options.endTime).toISOString()
        : options.endTime;
    params.set('end', end);
  }

  return params;
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Get message volume over time
 *
 * @param options - Query options
 * @param interval - Time interval (e.g., '1 hour', '1 day')
 * @returns Volume data points
 */
export async function getVolume(
  options: AnalyticsOptions,
  interval = '1h'
): Promise<VolumeDataPoint[]> {
  const params = buildAnalyticsParams(options);
  params.set('interval', interval);

  return serverGet<VolumeDataPoint[]>(`/analytics/volume?${params.toString()}`);
}

/**
 * Get message volume over time grouped by channel
 *
 * @param options - Query options
 * @param interval - Time interval (e.g., '1 hour', '1 day')
 * @returns Volume data grouped by channel ID
 */
export async function getVolumeByChannel(
  options: AnalyticsOptions,
  interval = '1d',
  signal?: AbortSignal
): Promise<VolumeByChannelData> {
  const params = buildAnalyticsParams(options);
  params.set('interval', interval);

  return serverGet<VolumeByChannelData>(
    `/analytics/volume-by-channel?${params.toString()}`,
    signal
  );
}

/**
 * Get heatmap data (activity by day of week and hour)
 *
 * @param options - Query options
 * @returns Heatmap data points
 */
export async function getHeatmap(
  options: AnalyticsOptions,
  signal?: AbortSignal
): Promise<HeatmapDataPoint[]> {
  const params = buildAnalyticsParams(options);
  return serverGet<HeatmapDataPoint[]>(`/analytics/heatmap?${params.toString()}`, signal);
}

/**
 * Get top speakers by message count
 *
 * @param options - Query options
 * @param limit - Max number of speakers to return
 * @returns Top speaker data
 */
export async function getTopSpeakers(
  options: AnalyticsOptions,
  limit = 10
): Promise<TopSpeakerData[]> {
  const params = buildAnalyticsParams(options);
  params.set('limit', String(limit));

  return serverGet<TopSpeakerData[]>(`/analytics/top-speakers?${params.toString()}`);
}

/**
 * Get channel distribution (messages per channel)
 *
 * @param options - Query options
 * @returns Channel distribution data
 */
export async function getChannelDistribution(
  options: AnalyticsOptions,
  signal?: AbortSignal
): Promise<ChannelDistributionData[]> {
  const params = buildAnalyticsParams(options);
  return serverGet<ChannelDistributionData[]>(`/analytics/channels?${params.toString()}`, signal);
}

/**
 * Get summary statistics
 *
 * @param options - Query options
 * @returns Summary stats
 */
export async function getSummaryStats(options: AnalyticsOptions): Promise<SummaryStats> {
  const params = buildAnalyticsParams(options);
  return serverGet<SummaryStats>(`/analytics/summary?${params.toString()}`);
}

/**
 * Get alert statistics
 *
 * @param options - Query options
 * @returns Alert stats
 */
export async function getAlertStats(options: AnalyticsOptions): Promise<AlertStats> {
  const params = buildAnalyticsParams(options);
  return serverGet<AlertStats>(`/analytics/alerts?${params.toString()}`);
}

/**
 * Get alert keyword time series - searches for keywords and returns time-bucketed counts
 *
 * @param options - Query options with keywords array
 * @param interval - Time interval (e.g., '1 day')
 * @returns Alert keyword time series data
 */
export async function getAlertKeywordVolume(
  options: AnalyticsOptions & { keywords?: string[] },
  interval = '1d',
  signal?: AbortSignal
): Promise<AlertKeywordData[]> {
  const params = buildAnalyticsParams(options);
  params.set('interval', interval);
  if (options.keywords?.length) {
    params.set('keywords', options.keywords.join(','));
  }
  return serverGet<AlertKeywordData[]>(`/analytics/alert-keywords?${params.toString()}`, signal);
}

/**
 * Get all analytics data in parallel
 * Convenience method for dashboard loading
 *
 * @param options - Query options
 * @returns All analytics data
 */
export async function getAllAnalytics(options: AnalyticsOptions): Promise<{
  volume: VolumeDataPoint[];
  heatmap: HeatmapDataPoint[];
  topSpeakers: TopSpeakerData[];
  channelDistribution: ChannelDistributionData[];
  summary: SummaryStats;
}> {
  const [volume, heatmap, topSpeakers, channelDistribution, summary] = await Promise.all([
    getVolume(options),
    getHeatmap(options),
    getTopSpeakers(options),
    getChannelDistribution(options),
    getSummaryStats(options)
  ]);

  return {
    volume,
    heatmap,
    topSpeakers,
    channelDistribution,
    summary
  };
}
